import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface Instructor {
  id: string;
  name: string;
  email: string;
}

interface InstructorListProps {
  isExpanded: boolean;
  loading: boolean;
  instructors: Instructor[];
  onToggleExpand: () => void;
}

export function InstructorList({
  isExpanded,
  loading,
  instructors,
  onToggleExpand,
}: InstructorListProps) {
  const handleExportCSV = () => {
    const headers = ["Instructor Name", "Email"];
    const rows = instructors.map((instructor) => [
      instructor.name,
      instructor.email,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `instructor_list_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Paper sx={{ mt: 4, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Instructors</Typography>
        <Box>
          {isExpanded && (
            <IconButton onClick={handleExportCSV} sx={{ mr: 1 }}>
              <FileDownloadIcon />
            </IconButton>
          )}
          <IconButton onClick={onToggleExpand}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Instructor Name</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : instructors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No instructors found
                  </TableCell>
                </TableRow>
              ) : (
                instructors.map((instructor) => (
                  <TableRow key={instructor.id}>
                    <TableCell>{instructor.name}</TableCell>
                    <TableCell>{instructor.email}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
}
